// Terminal interativo no navegador (6.1): o xterm.js ligado ao pseudo-terminal do servidor.
//
//   PtyEstado        pergunta ao servidor (GET /api/pty) se o terminal interativo pode ser usado e quais
//                    sessões existem; avisa quem escuta quando muda.
//   PtyTela          um xterm numa caixa: saída por SSE (GET /api/pty/:id/stream, que começa com o estado
//                    da tela), entrada e tamanho por POST. No celular ganha uma barra de teclas.
//   montaPtyJanela   a janela "Terminal interativo": abas das sessões, Nova sessão (claude ou codex numa
//                    pasta de projeto) e Encerrar com confirmação em dois cliques.
//
// A janela de conversa (live.ts) usa PtyTela quando o agente tem um pty: o xterm aparece no lugar da
// conversa. A lógica sem DOM está em pty-logica.ts (testada); o servidor, em src/http/pty.ts.

import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { t } from './i18n.js';
import { ehCelular } from './mobile.js';
import { DoisCliques, rotuloDaSessao, TECLAS, usoDoPty, type PtyEstadoApi, type PtySessao, type Uso } from './pty-logica.js';

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

interface Resposta {
  ok?: boolean;
  error?: string;
  id?: string;
  killed?: number;
}

/** POST JSON na API do terminal; nunca lança. */
export async function ptyPost(path: string, body: unknown = {}): Promise<Resposta> {
  const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
  const json = (await res?.json().catch(() => undefined)) as Resposta | undefined;
  return json ?? { ok: false, error: t('erro.nao_deu') };
}

// ------------------------------------------------------------------ estado

const ESTADO_MS = 8000;

export class PtyEstado {
  info: PtyEstadoApi | undefined;
  private readonly ouvintes = new Set<() => void>();
  private assinatura = '';
  private timer: number | undefined;

  constructor(private readonly isAdmin: () => boolean) {}

  get uso(): Uso {
    return usoDoPty(this.info);
  }

  get sessoes(): PtySessao[] {
    return this.info?.sessions ?? [];
  }

  onChange(ouvinte: () => void): void {
    this.ouvintes.add(ouvinte);
  }

  /** Lê o estado agora e passa a reler de tempos em tempos (com a aba visível). */
  start(): void {
    void this.refresh();
    // main.ts avisa quando o papel de quem entrou fica conhecido (o primeiro start roda antes do login)
    document.addEventListener('agzos:papel', () => void this.refresh());
    if (this.timer === undefined) this.timer = window.setInterval(() => !document.hidden && void this.refresh(), ESTADO_MS);
  }

  async refresh(): Promise<void> {
    let info: PtyEstadoApi | undefined;
    if (this.isAdmin()) {
      const res = await fetch('/api/pty').catch(() => null);
      info = res?.ok ? ((await res.json().catch(() => undefined)) as PtyEstadoApi | undefined) : undefined;
    }
    const assinatura = JSON.stringify(info ?? null);
    if (assinatura === this.assinatura) return;
    this.assinatura = assinatura;
    this.info = info;
    document.body.classList.toggle('pty-ok', this.uso.usavel);
    for (const ouvinte of this.ouvintes) ouvinte();
  }
}

// ------------------------------------------------------------------ a tela (xterm)

const TEMA = {
  background: '#0b0d12',
  foreground: '#d8dee9',
  cursor: '#9ad1ff',
  selectionBackground: '#33425e',
};

export class PtyTela {
  readonly el: HTMLElement;
  private readonly area: HTMLElement;
  private term: Terminal | undefined;
  private fit: FitAddon | undefined;
  private fonte: EventSource | undefined;
  private observador: ResizeObserver | undefined;
  private sessao: string | undefined;
  private fila = '';
  private enviando = false;
  private redimensiona: number | undefined;
  private terminou = false;

  constructor(
    host: HTMLElement,
    private readonly aoTerminar?: (id: string) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'pty-tela';
    this.area = document.createElement('div');
    this.area.className = 'pty-term';
    this.el.append(this.area);
    if (ehCelular()) this.el.append(this.teclas());
    host.append(this.el);
    // as teclas daqui são do terminal: não viram atalho do escritório nem fecham a janela
    for (const tipo of ['keydown', 'keyup', 'keypress'] as const) this.el.addEventListener(tipo, (e) => e.stopPropagation());
  }

  get id(): string | undefined {
    return this.sessao;
  }

  /** Liga a tela a uma sessão (desliga a anterior). */
  attach(id: string): void {
    if (this.sessao === id && this.fonte) return;
    this.detach();
    this.sessao = id;
    this.terminou = false;
    const term = new Terminal({
      cursorBlink: true,
      scrollback: 5000,
      fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
      fontSize: ehCelular() ? 13 : 14,
      theme: TEMA,
      allowProposedApi: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(this.area);
    this.term = term;
    this.fit = fit;
    term.write(`\x1b[2m${t('pty.conectando')}\x1b[0m`);
    term.onData((data) => this.envia(data));
    this.observador = new ResizeObserver(() => this.ajusta());
    this.observador.observe(this.area);

    const fonte = new EventSource(`/api/pty/${encodeURIComponent(id)}/stream`);
    this.fonte = fonte;
    fonte.addEventListener('screen', (e) => {
      const { screen, cols, rows } = JSON.parse((e as MessageEvent<string>).data) as { screen: string; cols: number; rows: number };
      // a tela do servidor tem o tamanho dele: desenha nesse tamanho e só depois ajusta ao da janela
      term.reset();
      term.resize(cols, rows);
      term.write(screen, () => this.ajusta(true));
    });
    fonte.addEventListener('data', (e) => term.write(JSON.parse((e as MessageEvent<string>).data) as string));
    fonte.addEventListener('exit', () => {
      this.terminou = true;
      fonte.close();
      term.write(`\r\n\x1b[2m${t('pty.terminou')}\x1b[0m\r\n`);
      this.aoTerminar?.(id);
    });
    // sessão que sumiu (404): o EventSource fecha sozinho; fora isso ele reconecta e recebe a tela de novo
    fonte.addEventListener('error', () => {
      if (fonte.readyState === EventSource.CLOSED && !this.terminou) this.aoTerminar?.(id);
    });
    requestAnimationFrame(() => this.ajusta());
  }

  detach(): void {
    this.fonte?.close();
    this.fonte = undefined;
    this.observador?.disconnect();
    this.observador = undefined;
    window.clearTimeout(this.redimensiona);
    this.term?.dispose();
    this.term = undefined;
    this.fit = undefined;
    this.area.replaceChildren();
    this.sessao = undefined;
    this.fila = '';
  }

  destroy(): void {
    this.detach();
    this.el.remove();
  }

  focus(): void {
    this.term?.focus();
  }

  /** Manda texto ao pty (teclas do celular, colar). */
  envia(data: string): void {
    if (!this.sessao || this.terminou) return;
    this.fila += data;
    if (!this.enviando) void this.descarrega();
  }

  /** Entradas em sequência (a ordem das teclas importa) e juntas (menos pedidos). */
  private async descarrega(): Promise<void> {
    this.enviando = true;
    const id = this.sessao;
    while (this.fila && id && this.sessao === id) {
      const lote = this.fila;
      this.fila = '';
      const r = await ptyPost(`/api/pty/${encodeURIComponent(id)}/input`, { data: lote });
      if (!r.ok) break;
    }
    this.enviando = false;
  }

  /** Cabe o xterm na caixa e avisa o servidor do novo tamanho (sem martelar durante o arrastar). */
  private ajusta(agora = false): void {
    const term = this.term;
    const id = this.sessao;
    if (!term || !this.fit || !id || !this.area.clientWidth || !this.area.clientHeight) return;
    try {
      this.fit.fit();
      // o fit calcula a altura da linha com fração e o xterm desenha com a linha inteira: sobrando, tira linhas
      const tela = this.area.querySelector<HTMLElement>('.xterm-screen');
      const cab = getComputedStyle(this.area);
      const util = this.area.clientHeight - parseFloat(cab.paddingTop) - parseFloat(cab.paddingBottom);
      for (let i = 0; tela && i < 3 && tela.offsetHeight > util + 0.5 && term.rows > 2; i++) term.resize(term.cols, term.rows - 1);
    } catch {
      return;
    }
    window.clearTimeout(this.redimensiona);
    const manda = () => void ptyPost(`/api/pty/${encodeURIComponent(id)}/resize`, { cols: term.cols, rows: term.rows });
    if (agora) manda();
    else this.redimensiona = window.setTimeout(manda, 150);
  }

  /** Barra de teclas do celular: o teclado virtual não tem Esc, Tab, setas nem Ctrl-C. */
  private teclas(): HTMLElement {
    const barra = document.createElement('div');
    barra.className = 'pty-teclas';
    barra.setAttribute('role', 'toolbar');
    barra.setAttribute('aria-label', t('pty.teclas'));
    const botoes: Array<[string, string]> = [
      ['esc', 'Esc'],
      ['tab', 'Tab'],
      ['ctrl-c', 'Ctrl-C'],
      ['cima', '↑'],
      ['baixo', '↓'],
      ['esquerda', '←'],
      ['direita', '→'],
      ['enter', '↵'],
    ];
    barra.innerHTML = botoes.map(([k, rotulo]) => `<button type="button" data-tecla="${k}">${esc(rotulo)}</button>`).join('') + `<button type="button" data-colar>${esc(t('pty.colar'))}</button>`;
    // pointerdown + preventDefault: o toque não tira o foco do terminal (o teclado não fecha)
    barra.addEventListener('pointerdown', (e) => {
      const alvo = (e.target as HTMLElement).closest<HTMLElement>('button');
      if (!alvo) return;
      e.preventDefault();
      const tecla = alvo.dataset.tecla;
      if (tecla) this.envia(TECLAS[tecla] ?? '');
      else void navigator.clipboard?.readText().then((texto) => texto && this.envia(texto)).catch(() => {});
    });
    return barra;
  }
}

// ------------------------------------------------------------------ a janela

export interface PtyJanelaDeps {
  estado: PtyEstado;
  isAdmin: () => boolean;
  /** Contas de CLI do CMS (para escolher a conta da Nova sessão). */
  contas: () => Array<{ id: string; kind: string; label?: string; enabled: boolean }>;
  /** Pastas sugeridas (as dos agentes no escritório). */
  pastas: () => string[];
}

export interface PtyJanela {
  open(sessao?: string): void;
  close(): void;
  readonly isOpen: boolean;
}

export function montaPtyJanela(host: HTMLElement, deps: PtyJanelaDeps): PtyJanela {
  const el = document.createElement('section');
  el.className = 'pty-jan';
  el.hidden = true;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', t('pty.titulo'));
  el.innerHTML = `<header class="pty-cab">
      <button class="pty-voltar" type="button" aria-label="${esc(t('vivo.voltar'))}">←</button>
      <span class="pty-logo" aria-hidden="true">&gt;_</span><b>${esc(t('pty.titulo'))}</b>
      <span class="pty-acoes">
        <button class="pty-max" type="button" aria-label="${esc(t('vivo.maximizar'))}" title="${esc(t('vivo.maximizar'))}">⤢</button>
        <button class="pty-fechar" type="button" aria-label="${esc(t('acao.fechar'))}">✕</button>
      </span></header>
    <nav class="pty-abas" role="tablist" aria-label="${esc(t('pty.aba_aria'))}"></nav>
    <p class="pty-aviso" role="status" hidden></p>
    <form class="pty-novo" hidden>
      <label>${esc(t('pty.cli'))} <select name="cli"><option value="claude">Claude Code</option><option value="codex">Codex</option></select></label>
      <label>${esc(t('pty.conta'))} <select name="conta"></select></label>
      <label class="pty-pasta">${esc(t('pty.pasta'))} <input name="pasta" required autocomplete="off" list="pty-pastas" placeholder="/var/www/meu-projeto" /></label>
      <datalist id="pty-pastas"></datalist>
      <label class="pty-pedido">${esc(t('pty.pedido'))} <input name="pedido" autocomplete="off" /></label>
      <button type="submit">${esc(t('pty.criar'))}</button>
    </form>
    <div class="pty-corpo"></div>
    <footer class="pty-rodape"><span class="pty-onde"></span><button class="pty-encerrar" type="button">${esc(t('pty.encerrar'))}</button></footer>
    <p class="pty-erro" role="alert"></p>`;
  host.append(el);

  const q = <T extends HTMLElement = HTMLElement>(sel: string): T => el.querySelector(sel) as T;
  const tela = new PtyTela(q('.pty-corpo'), () => void deps.estado.refresh());
  const confirma = new DoisCliques();
  let escolhida: string | undefined;
  let criando = false;
  let aberta = false;

  const sessoes = (): PtySessao[] => deps.estado.sessoes;
  const erro = (texto: string): void => {
    q('.pty-erro').textContent = texto;
  };

  function pinta(): void {
    const uso = deps.estado.uso;
    const lista = sessoes();
    if (!lista.some((s) => s.id === escolhida)) escolhida = lista[0]?.id;
    const aviso = q('.pty-aviso');
    aviso.hidden = uso.usavel;
    aviso.textContent = uso.usavel ? '' : uso.motivo === 'indisponivel' ? t('pty.sem_pty') : (uso.texto ?? t('erro.nao_deu'));
    q('.pty-abas').innerHTML =
      lista
        .map((s) => `<button type="button" role="tab" data-aba="${esc(s.id)}" aria-selected="${s.id === escolhida}" title="${esc(s.cwd)}">${esc(rotuloDaSessao(s))}</button>`)
        .join('') + `<button type="button" class="pty-mais" data-nova aria-selected="${criando}">+ ${esc(t('pty.nova'))}</button>`;
    // sem sessão, o formulário abre sozinho
    const form = q<HTMLFormElement>('.pty-novo');
    form.hidden = !uso.usavel || !(criando || lista.length === 0);
    q('.pty-corpo').hidden = !(uso.usavel && escolhida && !criando);
    if (uso.usavel && escolhida && !criando) {
      tela.attach(escolhida);
      const s = lista.find((x) => x.id === escolhida);
      q('.pty-onde').textContent = s ? `${s.cwd}${s.sessionId ? ` · ${s.sessionId.slice(0, 8)}` : ''}` : '';
    } else {
      tela.detach();
      q('.pty-onde').textContent = uso.usavel && lista.length === 0 && !criando ? t('pty.vazio') : '';
    }
    q('.pty-rodape').hidden = !escolhida || criando || !uso.usavel;
    // contas e pastas do formulário
    const contas = deps.contas().filter((c) => c.enabled && (c.kind === 'claude' || c.kind === 'codex'));
    const cli = q<HTMLSelectElement>('select[name=cli]').value;
    const conta = q<HTMLSelectElement>('select[name=conta]');
    const atual = conta.value;
    conta.innerHTML = `<option value="">${esc(t('pty.sem_conta'))}</option>` + contas.filter((c) => c.kind === cli).map((c) => `<option value="${esc(c.id)}">${esc(c.label ?? c.id)}</option>`).join('');
    conta.value = [...conta.options].some((o) => o.value === atual) ? atual : '';
    q('#pty-pastas').innerHTML = deps.pastas().map((p) => `<option value="${esc(p)}"></option>`).join('');
  }

  deps.estado.onChange(() => aberta && pinta());

  q('.pty-fechar').addEventListener('click', () => api.close());
  q('.pty-voltar').addEventListener('click', () => api.close());
  q('.pty-max').addEventListener('click', () => el.classList.toggle('max'));
  q('select[name=cli]').addEventListener('change', pinta);
  q('.pty-abas').addEventListener('click', (e) => {
    const alvo = e.target as HTMLElement;
    if (alvo.closest('[data-nova]')) {
      criando = true;
      pinta();
      q<HTMLInputElement>('input[name=pasta]').focus();
      return;
    }
    const aba = alvo.closest<HTMLElement>('[data-aba]')?.dataset.aba;
    if (aba) {
      criando = false;
      escolhida = aba;
      confirma.desarma();
      pinta();
      tela.focus();
    }
  });
  q('.pty-novo').addEventListener('submit', (e) => {
    e.preventDefault();
    const dados = new FormData(e.target as HTMLFormElement);
    const botao = q<HTMLButtonElement>('.pty-novo button[type=submit]');
    botao.disabled = true;
    erro('');
    void ptyPost('/api/pty/new', {
      cli: String(dados.get('cli')),
      cwd: String(dados.get('pasta')).trim(),
      prompt: String(dados.get('pedido')).trim() || undefined,
      account: String(dados.get('conta')) || undefined,
    }).then(async (r) => {
      botao.disabled = false;
      if (!r.ok || !r.id) return erro(r.error ?? t('erro.nao_deu'));
      criando = false;
      escolhida = r.id;
      (e.target as HTMLFormElement).reset();
      await deps.estado.refresh();
      pinta();
      tela.focus();
    });
  });
  q('.pty-encerrar').addEventListener('click', () => {
    const botao = q('.pty-encerrar');
    if (!escolhida) return;
    if (!confirma.clica()) {
      botao.textContent = t('pty.confirmar');
      botao.classList.add('armado');
      window.setTimeout(() => {
        if (!confirma.armado) return;
        confirma.desarma();
        botao.textContent = t('pty.encerrar');
        botao.classList.remove('armado');
      }, 4000);
      return;
    }
    botao.textContent = t('pty.encerrar');
    botao.classList.remove('armado');
    void ptyPost(`/api/pty/${encodeURIComponent(escolhida)}/kill`).then(async (r) => {
      erro(r.ok ? '' : (r.error ?? t('erro.nao_deu')));
      await deps.estado.refresh();
      pinta();
    });
  });
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !(e.target as HTMLElement).closest('.pty-tela')) api.close();
  });

  const api: PtyJanela = {
    open(sessao) {
      if (!deps.isAdmin()) return;
      aberta = true;
      el.hidden = false;
      if (sessao) {
        escolhida = sessao;
        criando = false;
      }
      void deps.estado.refresh().then(() => {
        if (aberta) pinta();
      });
      pinta();
    },
    close() {
      aberta = false;
      el.hidden = true;
      tela.detach();
    },
    get isOpen() {
      return aberta;
    },
  };

  // o botão "Terminal interativo" do painel do terminal (main.ts) abre a janela
  document.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-pty-abrir]')) api.open();
  });
  return api;
}
